import type { ApiBudget } from "../../api/types";
import { Card } from "../../components/Card";
import { t, tDynamic } from "../../i18n/t";

/**
 * xmg-xerobudget. How many source-API calls this migration needs, set against the vendor's daily budget, in plain
 * words: "This migration needs about 1,300 Xero calls ... Xero allows 1,000 per day ... so it will take about 2
 * days". Rendered from the structured record (never parsed out of the server's English `sentence`). Absent for a
 * source that cannot estimate its own call count and for a migration scoped before the estimate existed.
 */
/** Call counts, not money: thousands separators by hand (§B5.2 bans `toLocaleString`). */
export function groupCalls(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function ApiBudgetNotice({
  budget,
  bare = false,
}: {
  budget: ApiBudget | null | undefined;
  /** No card of its own: the collapsed section it sits in carries the title (xmg-actions-first). */
  bare?: boolean;
}) {
  if (budget === null || budget === undefined) return null;
  const vendor = budget.system.charAt(0).toUpperCase() + budget.system.slice(1);
  const number = groupCalls;
  const body = (
    <p className={bare ? undefined : "card__body"} data-testid="api-budget">
      {t("scope.api_budget_estimate", { calls: number(budget.est_calls), vendor })}{" "}
      {t("scope.api_budget_limit", {
        vendor,
        limit: number(budget.daily_limit),
        origin: tDynamic("scope.api_budget_origin.", budget.limit_source),
        remaining: number(budget.remaining_today),
      })}{" "}
      {budget.est_days <= 1
        ? t("scope.api_budget_fits")
        : t("scope.api_budget_days", { days: budget.est_days })}
    </p>
  );
  return bare ? body : <Card title={t("scope.api_budget_title")}>{body}</Card>;
}
